/**
 * NotesTab
 *
 * A simple scratchpad for project notes.
 * Same autosave pattern as the editor (1500ms debounce).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { StickyNote, CheckCircle, Loader2 } from 'lucide-react'

// Same debounce as main editor
const AUTOSAVE_DELAY = 1500

interface NotesTabProps {
  notes: string
  onSave: (content: string) => Promise<void>
  disabled?: boolean
}

export function NotesTab({ notes, onSave, disabled = false }: NotesTabProps) {
  const [content, setContent] = useState(notes)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync with external notes prop
  useEffect(() => {
    if (!hasChanges) {
      setContent(notes)
    }
  }, [notes, hasChanges])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const saveNotes = useCallback(
    async (newContent: string) => {
      if (disabled) return

      setIsSaving(true)
      try {
        await onSave(newContent)
        setLastSaved(new Date())
        setHasChanges(false)
      } catch (err) {
        console.error('Failed to save notes:', err)
      } finally {
        setIsSaving(false)
      }
    },
    [disabled, onSave]
  )

  const handleChange = (newContent: string) => {
    setContent(newContent)
    setHasChanges(true)

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Debounced save
    saveTimeoutRef.current = setTimeout(() => {
      saveNotes(newContent)
    }, AUTOSAVE_DELAY)
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-kids-border)]">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
          <h3 className="font-display font-bold text-[var(--color-kids-text)]">
            Notes
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {isSaving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin text-[var(--color-kids-text-muted)]" />
              <span className="text-[var(--color-kids-text-muted)]">Saving...</span>
            </>
          ) : hasChanges ? (
            <span className="text-[var(--color-kids-text-muted)]">Unsaved</span>
          ) : lastSaved ? (
            <>
              <CheckCircle className="w-3 h-3 text-[var(--color-kids-success)]" />
              <span className="text-[var(--color-kids-success)]">
                {formatTime(lastSaved)}
              </span>
            </>
          ) : null}
        </div>
      </div>

      {/* Notes textarea */}
      <div className="flex-1 p-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          placeholder="Jot down ideas, character names, plot points..."
          className="
            w-full h-full min-h-[200px] p-3
            bg-[var(--color-kids-surface-alt)]
            border border-[var(--color-kids-border)]
            focus:border-[var(--color-kids-primary)]
            rounded-[var(--radius-kid-sm)]
            text-sm text-[var(--color-kids-text)]
            placeholder:text-[var(--color-kids-text-muted)]
            resize-none outline-none transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          style={{ lineHeight: 1.6 }}
        />
      </div>

      {/* Helper text */}
      <div className="px-4 py-2 border-t border-[var(--color-kids-border)]">
        <p className="text-xs text-[var(--color-kids-text-muted)]">
          Notes are saved with your project
        </p>
      </div>
    </div>
  )
}
